// The store's offers — GET coupons/public (active, in date, under their
// cap, for this store). "Use at checkout" holds the code for the session;
// the checkout applies it and the server judges it on every quote (the
// channel, the minimum, first-order-only and the per-customer limit are
// its call, not this page's).

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { CHANNEL_LABEL, fetchPublicCoupons, offerText, type Channel, type PublicCoupon } from '../../lib/api';
import { cartCount, useCart } from '../../lib/cart';
import { holdCoupon, pendingCoupon } from '../../lib/offers';
import { AccountHead, accountPath } from './shared';

const channelsOf = (c: PublicCoupon): Channel[] => (['pickup', 'delivery', 'dine_in'] as Channel[]).filter((ch) => (ch === 'pickup' ? c.appliesPickup : ch === 'delivery' ? c.appliesDelivery : c.appliesDineIn));

export function Offers() {
  const { rid, sid } = useParams() as { rid: string; sid: string };
  const storeKey = `${rid}/${sid}`;
  const nav = useNavigate();
  const cart = useCart(storeKey);
  const [offers, setOffers] = useState<PublicCoupon[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [held, setHeld] = useState<string | null>(pendingCoupon(storeKey));

  useEffect(() => {
    fetchPublicCoupons(rid, sid).then(setOffers, (e) => setError(e instanceof Error ? e.message : 'Could not load the offers.'));
  }, [rid, sid]);

  function use(code: string) {
    holdCoupon(storeKey, code);
    setHeld(code);
    if (cartCount(cart) > 0) nav(`/r/${rid}/s/${sid}/checkout`);
  }

  return (
    <div className="co-page">
      <AccountHead back={accountPath(rid, sid)} backLabel="Back to your account" kicker="Your account" title="Offers" />
      {error && <div className="co-err acct-err" role="alert">{error}</div>}
      {offers === null && !error && <div className="loading">Looking…</div>}
      {offers?.length === 0 && <section className="co-sec"><p className="acct-quiet">Nothing on right now — check back soon.</p></section>}
      {held && (
        <div className="co-sec acct-held" data-held={held}>
          <span><b>{held}</b> is saved for checkout.</span>
          <Link to={`/r/${rid}/s/${sid}`} className="linky">Browse the menu →</Link>
        </div>
      )}
      <div data-section="offers">
        {offers?.map((c) => (
          <section className="co-sec acct-offer" key={c.code} data-offer={c.code}>
            <div className="acct-offer-what">{offerText(c)}</div>
            <div className="acct-offer-meta">
              {channelsOf(c).map((ch) => CHANNEL_LABEL[ch]).join(' · ')}
              {c.endsOn && ` · until ${new Date(`${c.endsOn}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`}
            </div>
            <div className="acct-offer-foot">
              <code className="acct-code">{c.code}</code>
              <button className="co-apply" aria-label={`Use ${c.code} at checkout`} aria-pressed={held === c.code} onClick={() => use(c.code)}>
                {held === c.code ? 'Saved ✓' : 'Use at checkout'}
              </button>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
